/**
 * Request Detail Page
 *
 * Displays request details and stage-specific UI based on workflow state.
 * Uses restored stage view components for full functionality.
 */

import type { Request } from '@cortex/database';
import {
  Badge,
  Button,
  Center,
  Container,
  Heading,
  Inline,
  Skeleton,
  Stack,
  Surface,
  Text,
} from '@cortex/ui';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { useCallback, useRef, useState } from 'react';

import {
  CanvasStepConnector,
  ClosedStageView,
  CreateStageView,
  DevelopStageView,
  FinalizeStageView,
  InterviewStageView,
  ReviewStageView,
  STAGE_CONFIG,
  WORKFLOW_STAGES,
} from '@/components/request';
import {
  useRequestByNumber,
  useUpdateRequestStatus,
} from '@/hooks/use-request-queries';

export const Route = createFileRoute('/_app/_main/requests/$id/')({
  component: RequestDetailPage,
});

function RequestDetailPage() {
  const { id } = Route.useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isTransitioning, setIsTransitioning] = useState(false);
  const stepRefs = useRef<(HTMLDivElement | null)[]>([]);

  const isNumeric = /^\d+$/.test(id);

  // Fetch request via API (includes project join for by-number)
  const {
    data: requestData,
    isPending: isLoadingRequest,
    error: requestError,
  } = useRequestByNumber(id);

  // Cast to Request type for stage components (they expect the base type)
  const request = requestData as Request | undefined;

  // Extract project from the joined response
  const project = requestData?.project ?? null;

  // Redirect UUID to request_number URL
  if (!isNumeric && requestData?.request_number) {
    navigate({
      to: '/requests/$id',
      params: { id: String(requestData.request_number) },
      replace: true,
    });
  }

  const updateStatus = useUpdateRequestStatus();

  // Handle request updates from stage components
  const handleRequestUpdate = useCallback(
    (updatedRequest: Request) => {
      queryClient.setQueryData(['request', id], updatedRequest);
      queryClient.invalidateQueries({ queryKey: ['request', id] });
    },
    [queryClient, id]
  );

  // Handle starting the interview
  const handleStartInterview = useCallback(async () => {
    if (!request) return;
    setIsTransitioning(true);
    try {
      await updateStatus.mutateAsync({
        requestId: request.id,
        status: 'interview',
      });
      handleRequestUpdate({ ...request, status: 'interview' });
    } catch (error) {
      console.error('Failed to start interview:', error);
    } finally {
      setIsTransitioning(false);
    }
  }, [request, handleRequestUpdate, updateStatus]);

  const handleBack = useCallback(() => {
    if (project) {
      navigate({ to: '/projects/$slug', params: { slug: project.slug } });
    } else {
      navigate({ to: '/' });
    }
  }, [project, navigate]);

  const currentStageIndex = request
    ? WORKFLOW_STAGES.indexOf(
        request.status as (typeof WORKFLOW_STAGES)[number]
      )
    : 0;

  if (isLoadingRequest) {
    return (
      <Stack gap="lg" className="min-h-screen p-6">
        <Skeleton className="mb-6 h-8 w-48" />
        <Skeleton className="h-4 w-64" />
      </Stack>
    );
  }

  if (requestError || !request) {
    return (
      <Center minHeight="50vh">
        <Stack gap="md" align="center">
          <Heading level={1} className="text-xl font-semibold">
            {requestError?.message ?? 'Request not found'}
          </Heading>
          <Button
            variant="link"
            onClick={() => navigate({ to: '/' })}
            className="mt-4"
          >
            Go back
          </Button>
        </Stack>
      </Center>
    );
  }

  const stageConfig = STAGE_CONFIG[request.status as keyof typeof STAGE_CONFIG];
  const StageIcon = stageConfig?.icon;

  return (
    <Stack gap="none" className="min-h-screen">
      {/* Header */}
      <Surface as="header" variant="bordered" className="bg-surface-1">
        <Container maxWidth="5xl" className="py-6">
          <Button
            variant="ghost"
            onClick={handleBack}
            className="text-foreground/60 hover:text-foreground mb-4 flex items-center gap-2 text-base"
          >
            <ArrowLeft className="h-4 w-4" />
            {project ? `Back to ${project.name}` : 'Back'}
          </Button>

          <Inline justify="between" align="start">
            <Inline gap="sm" align="center">
              <Center
                className={`h-10 w-10 rounded-lg ${stageConfig?.bgClass ?? 'bg-primary/10'}`}
              >
                {StageIcon && (
                  <StageIcon
                    className={`h-5 w-5 ${stageConfig?.textClass ?? 'text-primary'}`}
                  />
                )}
              </Center>
              <Stack gap="xs">
                <Heading level={1} className="text-2xl font-bold">
                  {request.title}
                </Heading>
                <Text color="muted" size="sm">
                  {stageConfig?.label ?? 'Unknown'} •{' '}
                  {stageConfig?.description ?? ''}
                </Text>
              </Stack>
            </Inline>
            <Badge
              className={`${stageConfig?.bgClass ?? 'bg-primary/10'} ${stageConfig?.textClass ?? 'text-primary'}`}
            >
              {stageConfig?.label ?? request.status}
            </Badge>
          </Inline>

          {/* Progress Bar */}
          <Stack gap="none" className="relative mt-6">
            <CanvasStepConnector
              stepRefs={stepRefs}
              currentStepIndex={currentStageIndex}
            />
            <Inline justify="between" align="center">
              {WORKFLOW_STAGES.map((stage, index) => {
                const config = STAGE_CONFIG[stage];
                const Icon = config.icon;
                const isComplete = index < currentStageIndex;
                const isCurrent = index === currentStageIndex;
                const isFuture = index > currentStageIndex;

                return (
                  <Stack key={stage} align="center" gap="xs">
                    <Center
                      ref={(el) => {
                        if (stepRefs.current) {
                          stepRefs.current[index] = el;
                        }
                      }}
                      className={`h-8 w-8 rounded-full ${
                        isComplete
                          ? 'bg-success text-white'
                          : isCurrent
                            ? `${config.bgClass} ${config.textClass} border-2 ${config.borderClass}`
                            : 'bg-surface-2 border-border text-foreground/50 border'
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </Center>
                    <Text
                      size="sm"
                      className={`mt-1 ${isFuture ? 'text-foreground/50' : 'text-foreground/70'}`}
                    >
                      {config.label}
                    </Text>
                  </Stack>
                );
              })}
            </Inline>
          </Stack>
        </Container>
      </Surface>

      {/* Main Content - Stage Views */}
      <Surface as="main">
        <Container maxWidth="5xl" className="py-8">
          {request.status === 'create' && (
            <CreateStageView
              request={request}
              onStartInterview={handleStartInterview}
              isTransitioning={isTransitioning}
            />
          )}

          {request.status === 'interview' && (
            <InterviewStageView
              request={request}
              onRequestUpdate={handleRequestUpdate}
            />
          )}

          {request.status === 'develop' && (
            <DevelopStageView
              request={request}
              onRequestUpdate={handleRequestUpdate}
            />
          )}

          {request.status === 'review' && (
            <ReviewStageView
              request={request}
              onRequestUpdate={handleRequestUpdate}
            />
          )}

          {request.status === 'finalize' && (
            <FinalizeStageView
              request={request}
              onRequestUpdate={handleRequestUpdate}
            />
          )}

          {request.status === 'closed' && <ClosedStageView request={request} />}
        </Container>
      </Surface>
    </Stack>
  );
}
